import { baseDomain } from 'src/config'

/**
 * @param {unknown} value Public app URL from env (may omit protocol).
 * @returns {string | null} Origin string without trailing slash, or null if unusable.
 */
export function normalizeToOrigin(value) {
  if (value == null || typeof value !== 'string') {
    return null
  }

  let s = value.trim()
  if (!s) {
    return null
  }

  if (!/^https?:\/\//i.test(s)) {
    s = `https://${s.replace(/^\/+/, '')}`
  }

  try {
    const url = new URL(s)
    return url.origin.replace(/\/+$/, '')
  } catch {
    return null
  }
}

/**
 * Origin for absolute app links (e.g. invite URLs).
 *
 * Order: `VITE_BASE_DOMAIN` (see `src/config`) normalized to an origin,
 * then `window.location.origin` when base domain is unset.
 *
 * @returns {string} Origin only, no trailing slash.
 */
export function resolveAppLinkOrigin() {
  const fromEnv = normalizeToOrigin(baseDomain)
  if (fromEnv) {
    return fromEnv
  }

  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin.replace(/\/+$/, '')
  }

  return ''
}

/**
 * Best-effort hostname from a user-typed URL or bare domain (path / query stripped).
 * @param {string} url
 * @returns {string}
 */
export function extractDomainFromUrl(url) {
  if (!url || !url.length) {
    return ''
  }

  let domain = url
  domain = domain.replace(/^https?:\/\//, '')
  domain = domain.replace(/^www\./, '')
  domain = domain.split('/')[0]
  domain = domain.split('?')[0]

  if (
    !domain ||
    domain.length < 2 ||
    domain.includes(' ') ||
    (!domain.includes('.') && domain.length < 3) ||
    !/^[a-zA-Z0-9.-]+$/.test(domain)
  ) {
    return ''
  }

  return domain
}

/**
 * @param {string} host
 * @returns {string}
 */
export function getFaviconUrl(host) {
  if (!host) return ''
  const params = new URLSearchParams({
    client: 'SOCIAL',
    type: 'FAVICON',
    fallback_opts: 'TYPE,SIZE,URL',
    url: `https://${host}`,
    size: '256',
  })
  return `https://t1.gstatic.com/faviconV2?${params.toString()}`
}

/**
 * Wraps a mutation's mutateAsync function to catch errors and resolve with undefined
 * instead of rejecting. This prevents Vue warnings about unhandled promise rejections
 * when errors are already handled by the mutation's onError handler.
 *
 * @param {Function} mutateAsync - The mutateAsync function from a Vue Query mutation
 * @returns {Function} - Wrapped function that resolves on error instead of rejecting
 */
export function wrapMutationAsync(mutateAsync) {
  return async (...args) => {
    try {
      return await mutateAsync(...args)
      // eslint-disable-next-line no-unused-vars
    } catch (error) {
      // Error already handled by onError, resolve instead of rejecting
      // to prevent Vue warnings about unhandled promise rejections
      return undefined
    }
  }
}

/**
 * Runs `fn` in parallel with a minimum wait. Rejects only after `minimumMs` elapses
 * so loading UI (e.g. login splash) does not flash on fast or failed requests.
 *
 * @template T
 * @param {() => Promise<T>} fn
 * @param {number} [minimumMs=500]
 * @returns {Promise<T>}
 */
export async function withMinimumDuration(fn, minimumMs = 500) {
  const [res] = await Promise.all([
    fn()
      .then((value) => ({ ok: true, value }))
      .catch((error) => ({ ok: false, error })),
    new Promise((r) => setTimeout(r, minimumMs)),
  ])
  if (!res.ok) throw res.error
  return res.value
}
